// Manufacturing projects (ERP-PLAN §5.12): one place from product to production, shared by the screens
// (web/src/lib/projects.ts), the server (server/src/projects.ts: the MCP's project_readiness, the setup command) so
// all of them agree. Everything here is worked out from synced rows; nothing is stored.
//
// - Part types and the production files each one expects (PART_FILES, missingFiles()).
// - marginFrom() / marginOf(): the Sell items margin rule, also used for a variant's roll-up cost.
// - indexProjects() once per change of the inputs, then projectScope() (a project's products, builds and parts, read
//   off one walk: shared/project-reach.ts) and readiness() (the eight setup steps, each done / needs work / not
//   started, with the concrete gaps).
// - A part's projects: the projects whose BOMs use it, never one of its own (partProjects(), inProject() for every
//   list's project filter, usedInIndex() for the projects and products a part ends up in). A sell item's: projectOfItem().
// - The build view: sectionsOf() (lines by module), lineRules() (rules in plain words), variantColumns() and
//   gridCells() (the variant grid).
import {
  type BomIndex,
  type BomItem,
  type BomLineRow,
  type BomRow,
  bomOf,
  byCm,
  conditionsOf,
  currentRevision,
  familyKey,
  lineCell,
  lineLabel,
  ownerLabel,
  productOptions,
  resolve,
  type Supply,
  variantOptions,
} from "./boms.ts";
import type {
  ChangeNotice,
  ChangeNoticeItem,
  ConsignShipment,
  ConsignShipmentLine,
  Item,
  Location,
  MakeOrder,
  Po,
  Project,
  ProjectProduct,
  ShopifyProduct,
  Spec,
  SpecFile,
  SpecRevision,
  SpecRevisionFile,
  Vendor,
  VendorItem,
} from "./db.gen.ts";
import { groupBy } from "./group.ts";
import { type PartProjects, type PartUse, type ProjectReach, partInProject, projectReach, type Reach, type ReachVia } from "./project-reach.ts";
import { type SpecFileKind, specsByItem } from "./specs.ts";

export type { PartProjects, PartUse } from "./project-reach.ts";

type Num = number | string | null | undefined;
const num = (v: Num) => (v == null || v === "" ? null : Number(v));
const bySku = (a: { sku: string }, b: { sku: string }) => a.sku.localeCompare(b.sku, undefined, { numeric: true, sensitivity: "base" });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Part types ---------------------------------------------------------------------------------------

export const PART_TYPES = ["pcb", "pcba", "molded", "metal", "fastener", "electronic", "packaging", "accessory", "other"] as const;
export type PartType = (typeof PART_TYPES)[number];
export const PART_TYPE_LABELS: Record<PartType, string> = {
  pcb: "PCB",
  pcba: "PCBA",
  molded: "Molded",
  metal: "Metal",
  fastener: "Fastener",
  electronic: "Electronic",
  packaging: "Packaging",
  accessory: "Accessory",
  other: "Other",
};

/** A file a part's spec should have: any one of `kinds` will do. Optional ones are suggested, never a gap. */
export type FileNeed = { label: string; kinds: readonly SpecFileKind[]; optional?: boolean };
const BOARD: readonly FileNeed[] = [
  { label: "Gerbers", kinds: ["pcb_fab"] },
  { label: "BOM + placement", kinds: ["pcba"] },
];
/**
 * What each part type's spec should hold (ERP-PLAN §5.12 step 6). Types not listed expect nothing yet. Packaging
 * depends on the kind of packaging and its printing: packagingOf(). Read a part's needs with partNeeds().
 */
export const PART_FILES: Record<Exclude<PartType, "packaging">, readonly FileNeed[]> = {
  pcb: BOARD,
  pcba: BOARD,
  molded: [
    { label: "STEP", kinds: ["step"] },
    { label: "Drawing", kinds: ["drawing"] },
  ],
  // There's no datasheet kind: a datasheet is filed as "other" (or a drawing).
  electronic: [{ label: "Datasheet or drawing", kinds: ["datasheet", "drawing", "other"], optional: true }],
  metal: [],
  fastener: [],
  accessory: [],
  other: [],
};

// Packaging --------------------------------------------------------------------------------------------
// A packaging part (part_type 'packaging') says what kind of packaging it is (item.pack_type) and how it's printed
// (item.pack_print, '' = the usual for its kind). Those two decide its print files, so a printed box asks for a
// dieline and its artwork, a label for its artwork, a bag whose label is its own part or a plain bag for nothing.

export const PACK_TYPES = ["box", "sleeve", "bag", "label", "insert", "sticker", "manual", "other"] as const;
export type PackType = (typeof PACK_TYPES)[number];
export const PACK_TYPE_LABELS: Record<PackType, string> = {
  box: "Box",
  sleeve: "Sleeve",
  bag: "Bag",
  label: "Label",
  insert: "Insert / foam",
  sticker: "Sticker",
  manual: "Manual / card",
  other: "Other packaging",
};
/** Plural, for the Parts library's packaging filter. */
export const PACK_TYPE_PLURALS: Record<PackType, string> = {
  box: "Boxes",
  sleeve: "Sleeves",
  bag: "Bags",
  label: "Labels",
  insert: "Inserts",
  sticker: "Stickers",
  manual: "Manuals",
  other: "Other",
};

export const PACK_PRINTS = ["artwork", "template", "text", "label", "none"] as const;
export type PackPrint = (typeof PACK_PRINTS)[number];
export const PACK_PRINT_LABELS: Record<PackPrint, string> = {
  artwork: "Printed from artwork",
  template: "HHL product label",
  text: "Text printed on it",
  label: "Plain, with a separate label",
  none: "Plain",
};

/** A kind that is itself a label (its HHL product label is the part); a bag or box with one carries it. */
const isLabelKind = (type: PackType | null) => type === "label" || type === "sticker";

/**
 * A printing choice in words for its kind: a bag or box "with an HHL product label" (one part, the label made per product
 * and stuck on), a label or sticker that is one.
 */
export const printLabel = (type: PackType | null, print: PackPrint) =>
  print === "template" && !isLabelKind(type) ? "Plain, with an HHL product label" : PACK_PRINT_LABELS[print];
/** The usual printing for each kind, while pack_print is ''. */
export const PACK_PRINT_DEFAULT: Record<PackType, PackPrint> = {
  box: "artwork",
  sleeve: "artwork",
  bag: "none",
  label: "artwork",
  insert: "none",
  sticker: "artwork",
  manual: "artwork",
  other: "none",
};
/** The printing each kind can have (a label isn't labelled; a manual is printed). The usual one first. */
export const PACK_PRINT_OPTIONS: Record<PackType, readonly PackPrint[]> = {
  box: ["artwork", "template", "label", "text", "none"],
  sleeve: ["artwork"],
  bag: ["none", "template", "label", "text", "artwork"],
  label: ["artwork", "template", "text"],
  insert: ["none", "artwork"],
  sticker: ["artwork", "template"],
  manual: ["artwork"],
  other: ["none", "artwork", "text", "label"],
};

/**
 * A packaging part's kind from its name ("GCU2 Box Sleeve" → sleeve, "FCC Label" → label), '' when it doesn't say.
 * The same rules migration 036 used on the parts there were; item.create / item.update / item.set_part_type use it when
 * a part becomes packaging without a kind.
 */
export function guessPackType(name: string): PackType | "" {
  if (/sleeve/i.test(name)) return "sleeve";
  if (/\b(manual|leaflet|booklet|instructions?|quick ?start|warranty card|thank[- ]you card)/i.test(name)) return "manual";
  if (/\bstickers?\b/i.test(name)) return "sticker";
  if (/\blabels?\b/i.test(name)) return "label";
  if (/\b(bags?|pouch(es)?|poly ?bags?)\b/i.test(name)) return "bag";
  if (/\b(foam|inserts?|trays?)\b/i.test(name)) return "insert";
  if (/\b(box(es)?|cartons?|mailers?)\b/i.test(name)) return "box";
  return "";
}

const DIELINE: FileNeed = { label: "Dieline", kinds: ["dieline"] };
const ARTWORK: FileNeed = { label: "Print artwork", kinds: ["artwork"] };

type PackFields = {
  part_type?: string | null;
  pack_type?: string | null;
  pack_print?: string | null;
  /** Product labels --- a generated label's size (template printing). */
  label_w_mm?: number | string | null;
  label_h_mm?: number | string | null;
};

export type Packaging = {
  /** null: not said yet (the part asks for it before its files can be known). */
  type: PackType | null;
  print: PackPrint;
  /** The print files its spec should have. */
  files: readonly FileNeed[];
  /** Whether it needs a spec at all: not for plain and labelled ones (nothing to tell the vendor but the part). */
  spec: boolean;
  /** "Printed box", "Bag with a label", "Plain bag": what it is, in a few words. */
  title: string;
  /** What that means for files, in a sentence. */
  says: string;
};

export const isPackagingPart = (item: Pick<PackFields, "part_type"> | undefined | null) => item?.part_type === "packaging";

/** A packaging part's kind, printing and the print files those ask for; null for other parts. */
export function packagingOf(item: PackFields): Packaging | null {
  if (!isPackagingPart(item)) return null;
  const type = (PACK_TYPES as readonly string[]).includes(item.pack_type ?? "") ? (item.pack_type as PackType) : null;
  const set = item.pack_print as PackPrint | "" | null | undefined;
  const print: PackPrint = set && (PACK_PRINTS as readonly string[]).includes(set) ? set : PACK_PRINT_DEFAULT[type ?? "other"];
  if (!type) return { type, print, files: [], spec: true, title: "Packaging", says: "Set its Category (box, bag, label…) to know its print files" };
  const name = PACK_TYPE_LABELS[type].toLowerCase().replace(" / foam", "").replace(" / card", "");
  // A box, sleeve or insert is cut to a shape: a printed one needs its dieline, an insert always (its cut shape).
  const cut = type === "insert" || ((type === "box" || type === "sleeve") && print === "artwork");
  const files = [...(cut ? [DIELINE] : []), ...(print === "artwork" ? [ARTWORK] : [])];
  const noun = type === "other" ? "packaging" : name;
  switch (print) {
    case "artwork":
      return {
        type,
        print,
        files,
        spec: true,
        title: `Printed ${noun}`,
        says: cut ? "Needs a dieline and the print artwork" : "Needs the print artwork",
      };
    case "text":
      return { type, print, files, spec: true, title: `${capitalize(noun)} with printed text`, says: "No file: write the words to print in its spec" };
    case "template": {
      // Product labels --- HHL Ops makes each product's label from the product (shared/product-labels.ts): no file, no spec.
      // A bag or box with one is one part (Mitch: bag + label "getting pretty messy"): the label is made for it and stuck on.
      const w = Number(item.label_w_mm);
      const h = Number(item.label_h_mm);
      const size = w > 0 && h > 0 ? `${w} × ${h} mm` : null;
      if (!isLabelKind(type)) {
        return {
          type,
          print,
          files,
          spec: files.length > 0,
          title: `${capitalize(noun)} with an HHL label`,
          says: size
            ? `No file: HHL Ops makes each product's label (${size}) to go on it`
            : "No file: HHL Ops makes each product's label to go on it, once its size is set",
        };
      }
      return {
        type,
        print,
        files: [],
        spec: false,
        title: "HHL product label",
        says: size ? `No file: HHL Ops makes each product's label at ${size}` : "No file: HHL Ops makes each product's label once its size is set",
      };
    }
    case "label":
      return {
        type,
        print,
        files,
        spec: files.length > 0,
        title: `${capitalize(noun)} with a label`,
        says: "No file: its label is its own part, with the artwork",
      };
    default:
      return {
        type,
        print,
        files,
        spec: files.length > 0,
        title: `Plain ${noun}`,
        says: files.length ? "Needs a dieline (its cut shape)" : "Nothing printed: no files needed",
      };
  }
}
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The files a part's spec should have, and whether it needs a spec at all (services and plain packaging don't). */
export function partNeeds(item: PackFields & { kind?: string }): { files: readonly FileNeed[]; spec: boolean } {
  if (item.kind === "service") return { files: [], spec: false };
  const pack = packagingOf(item);
  if (pack) return { files: pack.files, spec: pack.spec };
  return { files: PART_FILES[item.part_type as Exclude<PartType, "packaging">] ?? [], spec: true };
}

/** The expected files a spec doesn't have yet, given the kinds of its files that have an upload. */
export function missingFiles(item: PackFields, kinds: ReadonlySet<string>): { required: FileNeed[]; optional: FileNeed[] } {
  const missing = partNeeds(item).files.filter((f) => !f.kinds.some((k) => kinds.has(k)));
  return { required: missing.filter((f) => !f.optional), optional: missing.filter((f) => f.optional) };
}

/**
 * What a packaging part's released spec lacks for a vendor to print it (POs warn about these before they're sent):
 * "No released spec" when it needs files or text and has no released revision, "Label size not set" for an HHL product
 * label without its size, "No words to print" when text printing's
 * revision has no text, else the required print files the revision hasn't got. Empty when nothing's missing; null for
 * parts that aren't packaging.
 */
export function printGaps(item: PackFields, released: ReadonlySet<string> | null, words?: string | null): string[] | null {
  const pack = packagingOf(item);
  if (!pack) return null;
  if (!pack.type) return ["Category not set"];
  if (pack.print === "template") return Number(item.label_w_mm) > 0 && Number(item.label_h_mm) > 0 ? [] : ["Label size not set"];
  if (!pack.spec) return [];
  if (!released) return ["No released spec"];
  // Text printing: the words are the released revision's text (`words`: its English and 中文 together, when known).
  if (pack.print === "text" && words != null && !words.trim()) return ["No words to print"];
  return missingFiles(item, released).required.map((f) => f.label);
}

// Margin -------------------------------------------------------------------------------------------

/** Gross margin: (price − cost) ÷ price; null without a price above 0 or without a cost. */
export function marginFrom(price: Num, cost: Num): number | null {
  const p = num(price);
  const c = num(cost);
  return p != null && p > 0 && c != null ? (p - c) / p : null;
}

/** Sell items' margin column: Shopify's price against Shopify's cost (std_cost). */
export const marginOf = (i: { price: Num; std_cost: Num }) => marginFrom(i.price, i.std_cost);

// Data ----------------------------------------------------------------------------------------------

export type ProjectItem = Pick<
  Item,
  | "id"
  | "kind"
  | "source"
  | "sku"
  | "name"
  | "std_cost"
  | "price"
  | "project_id"
  | "project_link_id"
  | "part_type"
  | "pack_type"
  | "pack_print"
  | "archived"
  | "procurement"
  | "lifecycle"
  | "shopify_product_id"
  | "shopify_product_title"
  | "shopify_variant_title"
>;
export type ProductRow = Pick<ShopifyProduct, "id" | "title" | "handle" | "status" | "deleted" | "skip_reason">;

/** The synced rows readiness reads (the browser passes its tables; server/src/projects.ts queries the same with to_jsonb). */
export type ProjectData = {
  projects: readonly Project[];
  projectProducts: readonly ProjectProduct[];
  products: readonly ProductRow[];
  items: readonly ProjectItem[];
  vendors: readonly Pick<Vendor, "id" | "name" | "short_name" | "kind" | "lead_time_days" | "moq" | "archived">[];
  vendorItems: readonly Pick<VendorItem, "id" | "vendor_id" | "item_id" | "unit_price" | "price_breaks" | "moq" | "lead_time_days" | "is_preferred">[];
  specs: readonly Pick<Spec, "id" | "item_id" | "shopify_product_id" | "current_revision_id">[];
  specFiles: readonly Pick<SpecFile, "id" | "spec_id" | "kind" | "in_draft" | "current_version_id">[];
  specRevisions: readonly Pick<SpecRevision, "id" | "rev">[];
  /** Packaging --- each released revision's files (their kinds: what a vendor gets). */
  specRevisionFiles?: readonly Pick<SpecRevisionFile, "spec_revision_id" | "kind">[];
  locations: readonly Pick<Location, "id" | "name" | "kind" | "vendor_id" | "is_cm" | "active" | "ignored">[];
  /** Stock at every location: Shopify's levels (on_hand) and the ledger's balances (qty), one row each. */
  stock: readonly { item_id: string; location_id: string; qty: Num }[];
  shipments: readonly Pick<ConsignShipment, "id" | "number" | "cm_location_id" | "direction" | "status">[];
  shipmentLines: readonly Pick<ConsignShipmentLine, "shipment_id" | "item_id" | "qty">[];
  makeOrders: readonly Pick<MakeOrder, "id" | "po_id" | "item_id" | "cm_location_id" | "qty_output">[];
  pos: readonly Pick<Po, "id" | "number" | "status" | "kind">[];
  changeNotices: readonly Pick<ChangeNotice, "id" | "number" | "title" | "status" | "effective" | "effective_date" | "released_at" | "created_at">[];
  changeNoticeItems: readonly Pick<ChangeNoticeItem, "change_notice_id" | "item_id">[];
};

export type ProjectIndex = ReturnType<typeof indexProjects>;

/** Lookups for every project; build once per change of the rows (or of the BOM index). */
export function indexProjects(ix: BomIndex, d: ProjectData) {
  const itemById = new Map(d.items.map((i) => [i.id, i]));
  const linksByItem = groupBy(
    [...d.vendorItems].sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred)),
    (l) => l.item_id,
  );
  const specByItem = specsByItem(d.specs, d.items); // a variant's: its product's
  const fileKindsBySpec = new Map<string, Set<string>>();
  for (const f of d.specFiles) {
    if (!f.in_draft || !f.current_version_id) continue;
    let kinds = fileKindsBySpec.get(f.spec_id);
    if (!kinds) fileKindsBySpec.set(f.spec_id, (kinds = new Set()));
    kinds.add(f.kind);
  }
  const stockAt = new Map<string, Map<string, number>>(); // location → item → qty
  for (const s of d.stock) {
    let m = stockAt.get(s.location_id);
    if (!m) stockAt.set(s.location_id, (m = new Map()));
    m.set(s.item_id, (m.get(s.item_id) ?? 0) + Number(s.qty ?? 0));
  }
  const noticesByItem = groupBy(d.changeNoticeItems, (c) => c.item_id);
  return {
    ix,
    data: d,
    projectById: new Map(d.projects.map((p) => [p.id, p])),
    itemById,
    vendorById: new Map(d.vendors.map((v) => [v.id, v])),
    productById: new Map(d.products.map((p) => [p.id, p])),
    linksByItem,
    linksByProject: new Map(
      [...groupBy(d.projectProducts, (l) => l.project_id)].map(([k, v]) => [
        k,
        v.sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at)),
      ]),
    ),
    linkByProduct: new Map(d.projectProducts.map((l) => [l.shopify_product_id, l])),
    // Sell items by their own project (a part's projects come from the BOMs that use it: reachOf()).
    sellByProject: groupBy(
      d.items.filter((i) => i.source === "shopify" && i.project_id && !i.archived),
      (i) => i.project_id as string,
    ),
    specByItem,
    fileKindsBySpec,
    revisionById: new Map(d.specRevisions.map((r) => [r.id, r])),
    kindsByRevision: groupBy(d.specRevisionFiles ?? [], (f) => f.spec_revision_id),
    cmSitesByVendor: groupBy(
      d.locations.filter((l) => l.is_cm && l.active && !l.ignored && l.vendor_id),
      (l) => l.vendor_id as string,
    ),
    stockAt,
    shipmentsByLocation: groupBy(d.shipments, (s) => s.cm_location_id),
    linesByShipment: groupBy(d.shipmentLines, (l) => l.shipment_id),
    makeOrdersByItem: groupBy(d.makeOrders, (m) => m.item_id),
    poById: new Map(d.pos.map((p) => [p.id, p])),
    noticeById: new Map(d.changeNotices.map((c) => [c.id, c])),
    noticesByItem,
    scopes: new Map<string, ProjectScope | null>(),
    readiness: new Map<string, Readiness | null>(),
    /** reachOf(): worked out once, on first use. */
    reach: null as Reach | null,
  };
}

/** Every project's builds and parts, and each part's projects (shared/project-reach.ts), once per index. */
export function reachOf(px: ProjectIndex): Reach {
  px.reach ??= projectReach(
    px.ix,
    (productId) => px.linkByProduct.get(productId)?.project_id,
    (projectId) => px.projectById.get(projectId)?.name ?? projectId,
  );
  return px.reach;
}

/** Each local part's projects (in use, only on a draft), by project name; a part not in it is on no BOM and in no project. */
export const partProjects = (px: ProjectIndex): Map<string, PartProjects> => reachOf(px).byPart;

// Scope ---------------------------------------------------------------------------------------------

export type ScopeProduct = {
  link: ProjectProduct;
  product: ProductRow | undefined;
  title: string;
  /** Live variants, by SKU. */
  variants: BomItem[];
  options: { name: string; values: string[] }[];
  key: string;
  /** The revision in use (shared/boms.ts currentRevision), and every revision newest first. */
  bom: BomRow | undefined;
  revisions: BomRow[];
};

export type ScopeFamily = {
  key: string;
  bom: BomRow;
  label: string;
  kind: "product" | "subassembly";
  /** A sub-assembly with no maker of its own: built in the same order as what uses it (builtInside()). */
  inside: boolean;
  /** How it's in the project: a linked product's, a product BOM naming it, a sub-assembly nothing uses, or a line's. */
  via: ReachVia;
};
export type ScopePart = {
  item: ProjectItem;
  /** Lines in the project's builds (current revisions) that use it: as their part, in an option map or an override. */
  uses: PartUse[];
  supplies: Set<Supply>;
  /** A sub-assembly nothing uses, in the project through its own BOM's project (a kit, the dongle). */
  top: boolean;
};
export type ProjectScope = {
  project: Project;
  products: ScopeProduct[];
  /**
   * Every build at its current revision: the linked products', product BOMs that name the project while their product
   * is linked to none, sub-assemblies nothing uses whose BOM names it, and every sub-assembly those reach.
   */
  families: ScopeFamily[];
  /** Everything that goes into those builds, and the sub-assemblies nothing uses (top). */
  parts: Map<string, ScopePart>;
  /** Parts only a draft or scheduled revision of those builds uses: for lists and filters, never readiness or money. */
  onDraft: Map<string, { item: ProjectItem; uses: PartUse[] }>;
  variantIds: Set<string>;
  /** Sell items in the project whose Shopify product isn't linked to it. */
  unlinked: { productId: string; title: string; variants: number }[];
};

const isBought = (i: ProjectItem) => i.kind !== "subassembly";

/** A project's products, builds and parts. Cached on the index. */
export function projectScope(px: ProjectIndex, projectId: string): ProjectScope | null {
  if (px.scopes.has(projectId)) return px.scopes.get(projectId) ?? null;
  const project = px.projectById.get(projectId);
  if (!project) {
    px.scopes.set(projectId, null);
    return null;
  }
  const ix = px.ix;
  const products: ScopeProduct[] = (px.linksByProject.get(projectId) ?? []).map((link) => {
    const id = link.shopify_product_id;
    const variants = ix.variantsByProduct.get(id) ?? [];
    const key = `product:${id}`;
    return {
      link,
      product: px.productById.get(id),
      title: px.productById.get(id)?.title || ix.productTitle.get(id) || id,
      variants,
      options: productOptions(variants),
      key,
      bom: currentRevision(ix, key),
      revisions: ix.families.get(key) ?? [],
    };
  });
  const variantIds = new Set(products.flatMap((p) => p.variants.map((v) => v.id)));

  // The walk (shared/project-reach.ts): the linked products' builds in the order they're listed, then the rest.
  const reach = reachOf(px).byProject.get(projectId);
  const listed = new Map(products.map((p, n) => [p.key, n]));
  const families: ScopeFamily[] = [...(reach?.families.values() ?? [])]
    .map((f) => ({
      key: f.key,
      bom: f.bom,
      label: ownerLabel(ix, f.bom),
      kind: f.kind,
      inside: f.kind === "subassembly" && (f.bom.phantom || !f.bom.make_vendor_id),
      via: f.via,
    }))
    .sort((a, b) => (listed.get(a.key) ?? listed.size) - (listed.get(b.key) ?? listed.size));
  const parts = new Map<string, ScopePart>();
  for (const [id, p] of reach?.parts ?? []) {
    const item = px.itemById.get(id);
    if (item) parts.set(id, { item, uses: p.uses, supplies: p.supplies, top: p.top });
  }
  const onDraft = new Map<string, { item: ProjectItem; uses: PartUse[] }>();
  for (const [id, uses] of reach?.onDraft ?? []) {
    const item = px.itemById.get(id);
    if (item) onDraft.set(id, { item, uses });
  }

  const linked = new Set(products.map((p) => p.link.shopify_product_id));
  const unlinked = [
    ...groupBy(
      (px.sellByProject.get(projectId) ?? []).filter((i) => i.shopify_product_id && !linked.has(i.shopify_product_id)),
      (i) => i.shopify_product_id as string,
    ),
  ].map(([productId, vs]) => ({ productId, title: px.productById.get(productId)?.title || vs[0].shopify_product_title || productId, variants: vs.length }));

  const scope: ProjectScope = { project, products, families, parts, onDraft, variantIds, unlinked };
  px.scopes.set(projectId, scope);
  return scope;
}

/** The parts in a scope, by SKU. */
export const partList = (scope: ProjectScope) => [...scope.parts.values()].sort((a, b) => bySku(a.item, b.item));

// Readiness ------------------------------------------------------------------------------------------

export const STEP_KEYS = ["basics", "products", "build", "subassemblies", "sourcing", "files", "costs", "production"] as const;
export type StepKey = (typeof STEP_KEYS)[number];
export const STEP_LABELS: Record<StepKey, string> = {
  basics: "Basics",
  products: "Products",
  build: "Build",
  subassemblies: "Sub-assemblies",
  sourcing: "Sourcing",
  files: "Specs & files",
  costs: "Cost & margin",
  production: "CM",
};
export type StepStatus = "done" | "needs_work" | "not_started";
export const STEP_STATUS_LABELS: Record<StepStatus, string> = { done: "Done", needs_work: "Needs work", not_started: "Not started" };

/** Where a gap is fixed. The screens turn it into a link; the MCP passes it on. */
export type GapTarget =
  | { kind: "basics" }
  | { kind: "products" }
  | { kind: "bom"; id: string }
  | { kind: "item"; id: string }
  | { kind: "spec"; id: string }
  | { kind: "cm_site"; vendorId: string | null }
  | { kind: "consign"; locationId: string | null }
  | { kind: "make" };

export type GapItem = { id: string; label: string; sku?: string; detail?: string; target: GapTarget };
export type Gap = {
  key: string;
  /** "3 parts without a vendor": for the summary line and the checklist. */
  text: string;
  count: number;
  items: GapItem[];
  /** Where to fix the whole gap, when it isn't per item. */
  target?: GapTarget;
  /** A suggestion: shown, but it doesn't keep the step from being done. */
  optional?: boolean;
};
export type Step = { key: StepKey; label: string; status: StepStatus; gaps: Gap[] /** What's in place, in a few words each. */; have: string[] };

export type VariantCost = {
  variant: BomItem;
  bom: BomRow;
  cost: number;
  /** Every line has a cost and a part (resolve() found nothing to warn about). */
  complete: boolean;
  /** Lines whose cost is missing or incomplete, or that have no part for this variant. */
  missing: number;
  /** What resolve() left out ("Line 4 (GATE) has no part for Color = Funtastic Orange"), sub-assemblies' included. */
  warnings: string[];
  price: number | null;
  /** Roll-up cost against Shopify's price. */
  margin: number | null;
  /** Shopify's own cost (std_cost) and the Sell items margin it gives. */
  shopifyCost: number | null;
  shopifyMargin: number | null;
};

export type Readiness = {
  project: Project;
  steps: Step[];
  /** Steps done, of 8. */
  ready: number;
  total: number;
  /** "Ready to build: 6 of 8 — 3 parts without a vendor, 5 specs not released" */
  summary: string;
  costs: VariantCost[];
  numbers: {
    products: number;
    variants: number;
    parts: number;
    materials: number;
    subassemblies: number;
    services: number;
    builds: number;
    drafts: number;
    specsReleased: number;
    specsNeeded: number;
    cost: { min: number; max: number } | null;
    margin: { min: number; max: number } | null;
    consignedValue: number;
    consignedMissing: number;
    makeOrders: number;
    openMakeOrders: number;
  };
  cmSites: Pick<Location, "id" | "name">[];
  makeOrders: { id: string; po: ProjectData["pos"][number] | undefined; item: ProjectItem | undefined; open: boolean }[];
  notices: ProjectData["changeNotices"][number][];
};

const OPEN_PO = new Set(["draft", "sent", "acknowledged", "in_production", "shipped"]);
const gapOf = (key: string, text: string, items: GapItem[], extra: Partial<Gap> = {}): Gap[] =>
  items.length || extra.target ? [{ key, text, count: items.length || 1, items, ...extra }] : [];
const statusOf = (started: boolean, gaps: Gap[]): StepStatus => (!started ? "not_started" : gaps.some((g) => !g.optional) ? "needs_work" : "done");
const range = (values: number[]) => (values.length ? { min: Math.min(...values), max: Math.max(...values) } : null);
const itemLabel = (i: Pick<ProjectItem, "sku" | "name">) => (i.sku ? `${i.sku} · ${i.name}` : i.name);

/** The vendor link that counts for a part (preferred, else the first) and the terms it ends up with. */
export function sourcingOf(px: ProjectIndex, itemId: string) {
  const link = px.linksByItem.get(itemId)?.[0];
  const vendor = link ? px.vendorById.get(link.vendor_id) : undefined;
  const breaks = (link?.price_breaks as { unit_price: number }[] | null) ?? [];
  const price = num(link?.unit_price) ?? num(breaks[0]?.unit_price);
  const linkMoq = num(link?.moq);
  const moq = linkMoq ? linkMoq : num(vendor?.moq);
  const lead = num(link?.lead_time_days) ?? num(vendor?.lead_time_days);
  return {
    link,
    vendor,
    price,
    moq,
    moqFrom: linkMoq ? ("link" as const) : moq != null ? ("vendor" as const) : null,
    lead,
    leadFrom: link?.lead_time_days != null ? ("link" as const) : lead != null ? ("vendor" as const) : null,
  };
}

/** A part's spec: none, a draft never released, or released (with its letter), and the expected files it lacks. */
export function filesOf(px: ProjectIndex, item: ProjectItem) {
  const spec = px.specByItem.get(item.id);
  const rev = spec?.current_revision_id ? px.revisionById.get(spec.current_revision_id) : undefined;
  const kinds = (spec && px.fileKindsBySpec.get(spec.id)) ?? new Set<string>();
  // Packaging --- what the released revision has (what a vendor gets on a PO), beside the draft's uploads.
  const released = rev ? new Set((px.kindsByRevision.get(rev.id) ?? []).map((f) => f.kind as string)) : null;
  return { spec, rev: rev?.rev ?? null, kinds, released, missing: missingFiles(item, kinds), needs: partNeeds(item), pack: packagingOf(item) };
}

/** The eight setup steps for one project, each with what's in place and what's missing. Cached on the index. */
export function readiness(px: ProjectIndex, projectId: string): Readiness | null {
  if (px.readiness.has(projectId)) return px.readiness.get(projectId) ?? null;
  const scope = projectScope(px, projectId);
  if (!scope) {
    px.readiness.set(projectId, null);
    return null;
  }
  const ix = px.ix;
  const { project } = scope;
  const parts = partList(scope);
  const steps: Step[] = [];
  const cm = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
  const cmName = cm ? cm.short_name || cm.name : "";
  // The linked products' builds: a product BOM that only names the project doesn't start its Build step.
  const productFamilies = scope.families.filter((f) => f.via === "product");

  // 1. Basics: CM, status, launch date (and an owner, suggested).
  {
    const gaps = [
      ...gapOf("no_code", "No SKU prefix", [], { target: project.code ? undefined : { kind: "basics" } }),
      ...gapOf("no_cm", "No contract manufacturer chosen", [], { target: cm ? undefined : { kind: "basics" } }),
      ...gapOf("no_launch", "No launch date", [], { target: project.launch_date ? undefined : { kind: "basics" } }),
      ...gapOf("no_owner", "No owner", [], { target: project.owner_id ? undefined : { kind: "basics" }, optional: true }),
    ];
    const have = [
      cm && `CM ${cmName}`,
      project.launch_date && `Launch ${String(project.launch_date).slice(0, 10)}`,
      project.code && `SKU prefix ${project.code}`,
    ].filter((x): x is string => !!x);
    steps.push({ key: "basics", label: STEP_LABELS.basics, status: statusOf(!!(cm || project.launch_date || project.owner_id), gaps), gaps, have });
  }

  // 2. Products: a linked Shopify product, and a BOM for its variants.
  {
    const noBom: GapItem[] = scope.products
      .filter((p) => !p.bom && p.variants.length)
      .map((p) => ({ id: p.link.id, label: p.title, detail: plural(p.variants.length, "variant"), target: { kind: "products" } }));
    const leftOut: GapItem[] = scope.products
      .filter((p) => p.product?.deleted || p.product?.skip_reason || !p.variants.length)
      .map((p) => ({
        id: p.link.id,
        label: p.title,
        detail: p.product?.deleted ? "deleted in Shopify" : p.product?.skip_reason ? `left out: ${p.product.skip_reason}` : "no live variants",
        target: { kind: "products" },
      }));
    const variantsWithout = scope.products.filter((p) => !p.bom).reduce((n, p) => n + p.variants.length, 0);
    // A product BOM that names the project while its product is linked to none: its parts are in the project already.
    const listedUnlinked = new Set(scope.unlinked.map((u) => u.productId));
    const named: GapItem[] = scope.families
      .filter((f) => f.via === "bom" && !listedUnlinked.has(f.bom.shopify_product_id ?? ""))
      .map((f) => ({ id: f.bom.id, label: f.label, detail: "its BOM names this project; link it on Products", target: { kind: "products" } }));
    const gaps = [
      ...gapOf("no_product", "No Shopify product linked", [], { target: scope.products.length ? undefined : { kind: "products" } }),
      ...gapOf("no_bom", `${plural(variantsWithout, "variant")} without a BOM`, noBom),
      ...gapOf("left_out", `${plural(leftOut.length, "product")} with nothing to build`, leftOut),
      ...gapOf(
        "unlinked",
        `${plural(scope.unlinked.length, "product")} in this project but not linked`,
        scope.unlinked.map((u) => ({ id: u.productId, label: u.title, detail: plural(u.variants, "variant"), target: { kind: "products" } })),
        { optional: true },
      ),
      ...gapOf(
        "bom_unlinked",
        named.length === 1 ? "1 product to link: its BOM names this project" : `${named.length} products to link: their BOMs name this project`,
        named,
        { optional: true },
      ),
    ];
    const variants = scope.products.reduce((n, p) => n + p.variants.length, 0);
    const have = scope.products.length ? [`${plural(scope.products.length, "product")}, ${plural(variants, "variant")}`] : [];
    steps.push({ key: "products", label: STEP_LABELS.products, status: statusOf(scope.products.length > 0, gaps), gaps, have });
  }

  // 3. Build: released BOMs, rules that reach a variant, and a part for every variant on lines that pick by option.
  {
    const drafts: GapItem[] = scope.families
      .filter((f) => f.bom.status === "draft")
      .map((f) => ({ id: f.bom.id, label: f.label, detail: `Rev ${f.bom.revision} draft`, target: { kind: "bom", id: f.bom.id } }));
    // Every build in the project, each seen with the variants that reach it (a sub-assembly's lines see the options of
    // the variants it goes into, as resolve() has them); one nothing reaches is seen on its own.
    const noVariant: GapItem[] = [];
    const noPart: GapItem[] = [];
    const contexts = buildContexts(ix, scope);
    const order = scope.products[0]?.options ?? [];
    for (const fam of scope.families) {
      const seen = contexts.get(fam.key) ?? [];
      for (const line of ix.linesByBom.get(fam.bom.id) ?? []) {
        const cells = seen.map((c) => ({ c, cell: lineCell(ix, line, c.variantId, c.options) }));
        const where = `${fam.label}: ${line.section ? `${line.section} · ` : ""}${lineLabel(ix, line)}`;
        if (Object.keys(conditionsOf(line)).length && !cells.some((x) => x.cell.qty > 0))
          noVariant.push({
            id: line.id,
            label: where,
            detail: `${lineRule(line, order)} matches no variant${fam.kind === "subassembly" ? " it goes into" : ""}`,
            target: { kind: "bom", id: fam.bom.id },
          });
        const empty = cells.filter((x) => x.cell.qty > 0 && !x.cell.componentId);
        if (empty.length)
          noPart.push({
            id: line.id,
            label: where,
            detail: line.option_name
              ? `no part for ${[...new Set(empty.map((x) => x.cell.optionValue ?? "(none)"))].join(", ")}`
              : `no part (${plural(
                  empty.reduce((n, x) => n + x.c.variants, 0),
                  "variant",
                )})`,
            target: { kind: "bom", id: fam.bom.id },
          });
      }
    }
    const gaps = [
      ...gapOf("drafts", `${plural(drafts.length, "BOM")} not released`, drafts),
      ...gapOf("no_variant", `${plural(noVariant.length, "line")} whose rule matches no variant`, noVariant),
      ...gapOf("no_part", `${plural(noPart.length, "line")} missing a part for some variants`, noPart),
    ];
    const active = scope.families.length - drafts.length;
    const have = scope.families.length ? [`${plural(scope.families.length, "BOM")}: ${active} released, ${drafts.length} draft`] : [];
    steps.push({ key: "build", label: STEP_LABELS.build, status: statusOf(productFamilies.length > 0, gaps), gaps, have });
  }

  // 4. Sub-assemblies: each has its own BOM (one with no maker of its own is built in the same order as what uses it).
  {
    const subs = parts.filter((p) => p.item.kind === "subassembly");
    const noBom: GapItem[] = [];
    for (const p of subs) {
      const b = currentRevision(ix, `item:${p.item.id}`);
      if (!b) noBom.push({ id: p.item.id, label: itemLabel(p.item), sku: p.item.sku, target: { kind: "item", id: p.item.id } });
    }
    const gaps = [...gapOf("no_bom", `${plural(noBom.length, "sub-assembly", "sub-assemblies")} without a BOM`, noBom)];
    const have = subs.length ? [`${plural(subs.length, "sub-assembly", "sub-assemblies")}, ${subs.length - noBom.length} with a BOM`] : ["No sub-assemblies"];
    steps.push({ key: "subassemblies", label: STEP_LABELS.subassemblies, status: statusOf(productFamilies.length > 0 || subs.length > 0, gaps), gaps, have });
  }

  // 5. Sourcing: every part HHL buys has a vendor, a price, an MOQ and a lead time (vendor defaults count); services no
  //    MOQ. A part only the CM buys or makes is the CM's to source (Mitch, 2026-11-03: "it should clear because Elecrow is
  //    contracted to handle that sourcing and or make"): nothing asked of it.
  {
    const made = (id: string) => {
      const i = ix.itemById.get(id);
      return !!i && i.kind === "sell" && !!bomOf(ix, i);
    };
    const bought = parts.filter((p) => isBought(p.item) && !made(p.item.id));
    const no = { vendor: [] as GapItem[], price: [] as GapItem[], moq: [] as GapItem[], lead: [] as GapItem[] };
    let linked = 0;
    for (const p of bought) {
      const s = sourcingOf(px, p.item.id);
      const g = (detail?: string): GapItem => ({ id: p.item.id, label: itemLabel(p.item), sku: p.item.sku, detail, target: { kind: "item", id: p.item.id } });
      if ([...p.supplies].every(byCm)) continue; // the CM buys or makes it: the CM's to source
      if (!s.link) {
        no.vendor.push(g());
        continue;
      }
      linked++;
      if (s.price == null) no.price.push(g(s.vendor ? s.vendor.short_name || s.vendor.name : undefined));
      if (p.item.kind === "service") continue;
      if (s.moq == null) no.moq.push(g());
      if (s.lead == null) no.lead.push(g());
    }
    const gaps = [
      ...gapOf("no_vendor", `${plural(no.vendor.length, "part")} without a vendor`, no.vendor),
      ...gapOf("no_price", `${plural(no.price.length, "part")} without a price`, no.price),
      ...gapOf("no_moq", `${plural(no.moq.length, "part")} without an MOQ`, no.moq),
      ...gapOf("no_lead", `${plural(no.lead.length, "part")} without a lead time`, no.lead),
    ];
    const have = bought.length ? [`${linked} of ${plural(bought.length, "bought part")} have a vendor`] : [];
    steps.push({ key: "sourcing", label: STEP_LABELS.sourcing, status: statusOf(linked > 0, gaps), gaps, have });
  }

  // 6. Production files: each part has a released spec with the files its type expects. Packaging --- plain and
  // labelled packaging needs no spec; packaging whose kind isn't said yet is asked for it.
  let specsReleased = 0;
  const needSpec = parts.filter((p) => partNeeds(p.item).spec);
  {
    const noSpec: GapItem[] = [];
    const unreleased: GapItem[] = [];
    const missing: GapItem[] = [];
    const optional: GapItem[] = [];
    const packKind: GapItem[] = parts
      .filter((p) => packagingOf(p.item)?.type === null)
      .map((p) => ({ id: p.item.id, label: itemLabel(p.item), sku: p.item.sku, detail: "box, bag, label…", target: { kind: "item", id: p.item.id } }));
    let started = false;
    for (const p of needSpec) {
      const f = filesOf(px, p.item);
      const g = (detail?: string): GapItem => ({ id: p.item.id, label: itemLabel(p.item), sku: p.item.sku, detail, target: { kind: "spec", id: p.item.id } });
      if (!f.spec) {
        noSpec.push(g(f.pack?.type ? f.pack.title : PART_TYPE_LABELS[p.item.part_type as PartType]));
        continue;
      }
      started = true;
      if (f.rev) specsReleased++;
      else unreleased.push(g("draft"));
      // Packaging --- once released, what counts is the released revision (what a vendor gets on a PO); a file only
      // in the draft says so.
      const lacks = f.released ? missingFiles(p.item, f.released) : f.missing;
      const named = (m: FileNeed) => (f.released && m.kinds.some((k) => f.kinds.has(k)) ? `${m.label} (not released)` : m.label);
      if (lacks.required.length) missing.push(g(lacks.required.map(named).join(", ")));
      if (lacks.optional.length) optional.push(g(lacks.optional.map(named).join(", ")));
    }
    const gaps = [
      ...gapOf("no_spec", `${plural(noSpec.length, "part")} without a spec`, noSpec),
      ...gapOf("unreleased", `${plural(unreleased.length, "spec")} not released`, unreleased),
      ...gapOf("missing_files", `${plural(missing.length, "spec")} missing files their category needs`, missing),
      ...gapOf("pack_kind", `${plural(packKind.length, "packaging part")} without a Category`, packKind),
      ...gapOf("optional_files", `${plural(optional.length, "spec")} could use a suggested file`, optional, { optional: true }),
    ];
    const have = needSpec.length ? [`${needSpec.length - noSpec.length} of ${plural(needSpec.length, "part")} have a spec, ${specsReleased} released`] : [];
    steps.push({ key: "files", label: STEP_LABELS.files, status: statusOf(started, gaps), gaps, have });
  }

  // 7. Cost & margin: every variant's roll-up is complete, and its margin against Shopify's price.
  const costs: VariantCost[] = [];
  {
    const missingParts = new Map<string, GapItem>();
    for (const p of scope.products) {
      if (!p.bom) continue;
      for (const v of p.variants) {
        const r = resolve(ix, p.bom, v);
        for (const l of r.lines)
          if (!l.phantom && (l.extCost == null || !l.costComplete))
            missingParts.set(l.itemId, {
              id: l.itemId,
              label: itemLabel(l.item),
              sku: l.item.sku,
              detail: l.stocked ? "its BOM has parts without a cost" : "no cost or vendor price",
              target: { kind: "item", id: l.itemId },
            });
        const price = num(v.price);
        // A line without a part costs nothing in resolve(), so a variant with one isn't fully costed either.
        const complete = r.cost.complete && !r.warnings.length;
        costs.push({
          variant: v,
          bom: p.bom,
          cost: r.cost.total,
          complete,
          missing: r.cost.missing + r.warnings.length,
          warnings: r.warnings,
          price,
          margin: complete ? marginFrom(price, r.cost.total) : null,
          shopifyCost: num(v.std_cost),
          shopifyMargin: marginOf(v),
        });
      }
    }
    const incomplete = costs.filter((c) => !c.complete);
    const negative = costs.filter((c) => c.margin != null && c.margin < 0);
    const variantItem = (c: VariantCost): GapItem => ({
      id: c.variant.id,
      label: c.variant.shopify_variant_title || c.variant.name,
      sku: c.variant.sku,
      detail: c.complete ? undefined : `${plural(c.missing, "line")} without a cost or a part`,
      target: { kind: "bom", id: c.bom.id },
    });
    const gaps = [
      ...gapOf("missing_costs", `${plural(incomplete.length, "variant")} with missing costs`, incomplete.map(variantItem)),
      ...gapOf(
        "parts_without_cost",
        `${plural(missingParts.size, "part")} without a cost`,
        [...missingParts.values()].sort((a, b) => a.label.localeCompare(b.label)),
      ),
      ...gapOf("negative_margin", `${plural(negative.length, "variant")} costing more than its price`, negative.map(variantItem)),
    ];
    const margins = costs.flatMap((c) => (c.margin == null ? [] : [c.margin]));
    const have = costs.length ? [`${costs.length - incomplete.length} of ${plural(costs.length, "variant")} fully costed`] : [];
    if (margins.length) have.push(`margin ${Math.round(Math.min(...margins) * 100)}–${Math.round(Math.max(...margins) * 100)}%`);
    steps.push({ key: "costs", label: STEP_LABELS.costs, status: statusOf(costs.length > 0, gaps), gaps, have });
  }

  // 8. Production: the CM's site, something consigned there, and a pilot make order.
  const cmSites = cm ? (px.cmSitesByVendor.get(cm.id) ?? []) : [];
  const inScope = (id: string) => scope.parts.has(id) || scope.variantIds.has(id);
  let consignedValue = 0;
  let consignedMissing = 0;
  let consigned = false;
  for (const site of cmSites) {
    for (const [itemId, qty] of px.stockAt.get(site.id) ?? []) {
      if (qty <= 0 || !inScope(itemId)) continue;
      consigned = true;
      const item = px.itemById.get(itemId);
      const unit = num(item?.std_cost) ?? ix.vendorPrice.get(itemId) ?? null;
      if (unit == null) consignedMissing++;
      else consignedValue += unit * qty;
    }
    for (const s of px.shipmentsByLocation.get(site.id) ?? [])
      if (s.direction === "to_cm" && (px.linesByShipment.get(s.id) ?? []).some((l) => inScope(l.item_id))) consigned = true;
  }
  const makeOrders = [...scope.variantIds, ...[...scope.parts.values()].filter((p) => p.item.kind === "subassembly").map((p) => p.item.id)].flatMap((id) =>
    (px.makeOrdersByItem.get(id) ?? []).map((m) => {
      const po = px.poById.get(m.po_id);
      return { id: m.id, po, item: px.itemById.get(m.item_id), open: !!po && OPEN_PO.has(po.status) };
    }),
  );
  const pilot = makeOrders.filter((m) => m.po?.status !== "cancelled");
  {
    const gaps = [
      ...gapOf("no_cm", "No contract manufacturer chosen", [], { target: cm ? undefined : { kind: "basics" } }),
      ...gapOf("no_site", `${cmName || "The CM"} has no CM site set up`, [], {
        target: cm && !cmSites.length ? { kind: "cm_site", vendorId: cm.id } : undefined,
      }),
      ...gapOf("nothing_consigned", `Nothing consigned to ${cmName || "the CM"} yet`, [], {
        target: cmSites.length && !consigned ? { kind: "consign", locationId: cmSites[0].id } : undefined,
      }),
      ...gapOf("no_pilot", "No pilot make order", [], { target: pilot.length ? undefined : { kind: "make" } }),
    ];
    const have = [
      cmSites.length && `CM site ${cmSites.map((s) => s.name).join(", ")}`,
      consigned && "parts consigned",
      pilot.length && plural(pilot.length, "make order"),
    ].filter((x): x is string => !!x);
    steps.push({ key: "production", label: STEP_LABELS.production, status: statusOf(cmSites.length > 0 || consigned || pilot.length > 0, gaps), gaps, have });
  }

  const ready = steps.filter((s) => s.status === "done").length;
  const top = steps
    .filter((s) => s.status !== "done")
    .flatMap((s) => s.gaps.filter((g) => !g.optional).slice(0, 1))
    .slice(0, 2)
    .map((g) => lowerFirst(g.text));
  const notices = [...new Set([...scope.parts.keys(), ...scope.variantIds].flatMap((id) => (px.noticesByItem.get(id) ?? []).map((n) => n.change_notice_id)))]
    .flatMap((id) => px.noticeById.get(id) ?? [])
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const margins = costs.flatMap((c) => (c.margin == null ? [] : [c.margin]));
  const out: Readiness = {
    project,
    steps,
    ready,
    total: steps.length,
    summary: `Ready to build: ${ready} of ${steps.length}${top.length ? ` — ${top.join(", ")}` : ""}`,
    costs,
    numbers: {
      products: scope.products.length,
      variants: scope.variantIds.size,
      parts: parts.length,
      materials: parts.filter((p) => p.item.kind === "material").length,
      subassemblies: parts.filter((p) => p.item.kind === "subassembly").length,
      services: parts.filter((p) => p.item.kind === "service").length,
      builds: scope.families.length,
      drafts: scope.families.filter((f) => f.bom.status === "draft").length,
      specsReleased,
      specsNeeded: needSpec.length,
      cost: range(costs.filter((c) => c.complete).map((c) => c.cost)),
      margin: range(margins),
      consignedValue,
      consignedMissing,
      makeOrders: pilot.length,
      openMakeOrders: makeOrders.filter((m) => m.open).length,
    },
    cmSites,
    makeOrders,
    notices,
  };
  px.readiness.set(projectId, out);
  return out;
}

/** How a build is seen: one variant's options (product lines), or the options of the variants a sub-assembly goes into. */
type BuildContext = { variantId: string | null; options: Record<string, string>; variants: number };

/** Each build in a scope with the variants that reach it, through phantom and stocked sub-assemblies alike. */
function buildContexts(ix: BomIndex, scope: ProjectScope): Map<string, BuildContext[]> {
  const out = new Map<string, Map<string, BuildContext>>();
  const queue: { key: string; bom: BomRow; ctx: BuildContext }[] = [];
  const add = (bom: BomRow, ctx: BuildContext) => {
    const key = familyKey(bom);
    let m = out.get(key);
    if (!m) out.set(key, (m = new Map()));
    const id = `${ctx.variantId ?? ""}|${JSON.stringify(Object.entries(ctx.options).sort())}`;
    const had = m.get(id);
    if (had) {
      had.variants += ctx.variants;
      return;
    }
    const copy = { ...ctx };
    m.set(id, copy);
    queue.push({ key, bom, ctx: copy });
  };
  for (const p of scope.products) if (p.bom) for (const v of p.variants) add(p.bom, { variantId: v.id, options: variantOptions(v), variants: 1 });
  // A product BOM that names the project while its product isn't linked is seen with its own variants too.
  for (const f of scope.families)
    if (f.via === "bom" && f.bom.shopify_product_id)
      for (const v of ix.variantsByProduct.get(f.bom.shopify_product_id) ?? []) add(f.bom, { variantId: v.id, options: variantOptions(v), variants: 1 });
  for (let at = 0; at < queue.length; at++) {
    const { bom, ctx } = queue[at];
    for (const line of ix.linesByBom.get(bom.id) ?? []) {
      const cell = lineCell(ix, line, ctx.variantId, ctx.options);
      const item = cell.qty > 0 && cell.componentId ? ix.itemById.get(cell.componentId) : undefined;
      const sub = item && item.source === "local" ? bomOf(ix, item) : undefined;
      if (sub && sub.id !== bom.id) add(sub, { variantId: null, options: ctx.options, variants: 1 });
    }
  }
  // A build nothing in the project reaches (a kit, the dongle) is seen as itself.
  for (const f of scope.families) if (!out.has(f.key)) out.set(f.key, new Map([["self", { variantId: f.bom.item_id, options: {}, variants: 1 }]]));
  return new Map([...out].map(([k, m]) => [k, [...m.values()]]));
}

const lowerFirst = (s: string) => (/^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s);

// The build view -----------------------------------------------------------------------------------------

export const NO_SECTION = "";

/** Packaging --- the group every packaging line is in (isPackagingRow()), whatever its module says. */
export const PACKAGING_SECTION = "Packaging";
export type BomSection = { name: string; lines: BomLineRow[]; packaging: boolean };

/**
 * A revision's lines by module: the revision's own order first, then modules in line order, lines without one last.
 * Packaging --- with `isPackaging`, packaging lines sit in their own Packaging group: where a module of that name is
 * in the order, else last. Other lines a module of that name holds stay apart as "Packaging (not packaging parts)".
 */
export function sectionsOf(bom: Pick<BomRow, "section_order">, lines: readonly BomLineRow[], isPackaging?: (line: BomLineRow) => boolean): BomSection[] {
  const packKey = PACKAGING_SECTION.toLowerCase();
  const sectionOf = (l: BomLineRow) => {
    if (isPackaging?.(l)) return PACKAGING_SECTION;
    const s = (l.section ?? "").trim();
    return isPackaging && s.toLowerCase() === packKey ? `${PACKAGING_SECTION} (not packaging parts)` : s;
  };
  const by = groupBy(lines, sectionOf);
  const named = (s: string) => (isPackaging && s.trim().toLowerCase() === packKey ? PACKAGING_SECTION : s.trim());
  const order = [...(bom.section_order ?? []).map(named), ...lines.map(sectionOf)].filter((s) => s !== NO_SECTION);
  const names = [...new Set(order)].filter((s) => by.has(s) && s !== PACKAGING_SECTION);
  if (by.has(NO_SECTION)) names.push(NO_SECTION);
  // Packaging goes where the revision's order puts a Packaging module, else after everything else.
  if (by.has(PACKAGING_SECTION)) {
    const at = (bom.section_order ?? []).findIndex((s) => s.trim().toLowerCase() === packKey);
    const before = at < 0 ? -1 : names.findIndex((n) => (bom.section_order ?? []).slice(at + 1).some((s) => s.trim() === n));
    names.splice(before < 0 ? names.length : before, 0, PACKAGING_SECTION);
  }
  return names.map((name) => ({ name, lines: by.get(name) ?? [], packaging: name === PACKAGING_SECTION && !!isPackaging }));
}

/**
 * "Wireless only", "Wired or Wireless", "Wireless · Ghost Black only": a line's conditions in plain words, in the
 * product's option order when it's given (jsonb keeps its own key order).
 */
export function lineRule(line: Pick<BomLineRow, "applies_when">, options: readonly { name: string }[] = []): string {
  const rank = (name: string) => {
    const i = options.findIndex((o) => o.name.toLowerCase() === name.toLowerCase());
    return i < 0 ? options.length : i;
  };
  const c = Object.entries(conditionsOf(line)).sort(([a], [b]) => rank(a) - rank(b));
  return c.length ? `${c.map(([, values]) => values.join(" or ")).join(" · ")} only` : "Every variant";
}

export type RuleChip = { kind: "when" | "option" | "override"; text: string; title?: string };

/**
 * A line's rules as chips, in plain words: "Wireless only" (its conditions; none when they cover every variant),
 * "by Color: 5 parts" (the parts an option picks), "2 variant changes" (overrides).
 */
export function lineRules(ix: BomIndex, line: BomLineRow, variants: readonly BomItem[]): RuleChip[] {
  const out: RuleChip[] = [];
  const conditions = conditionsOf(line);
  if (Object.keys(conditions).length) {
    const options = productOptions(variants);
    const narrows = Object.entries(conditions).some(([name, values]) => {
      const all = options.find((o) => o.name.toLowerCase() === name.toLowerCase())?.values ?? [];
      return !all.length || all.some((v) => !values.some((x) => x.toLowerCase() === v.toLowerCase()));
    });
    if (narrows)
      out.push({
        kind: "when",
        text: lineRule(line, options),
        title: Object.entries(conditions)
          .map(([k, v]) => `${k} = ${v.join(", ")}`)
          .join("; "),
      });
  }
  if (line.option_name) {
    const picks = new Map<string, string[]>(); // part → values
    const values = productOptions(variants).find((o) => o.name.toLowerCase() === line.option_name.toLowerCase())?.values;
    const all = values ?? [...(ix.mapsByLine.get(line.id)?.keys() ?? [])];
    for (const value of all) {
      const id = ix.mapsByLine.get(line.id)?.get(value.toLowerCase()) ?? line.component_item_id;
      if (id) picks.set(id, [...(picks.get(id) ?? []), value]);
    }
    const sku = (id: string) => ix.itemById.get(id)?.sku || ix.itemById.get(id)?.name || "?";
    out.push({
      kind: "option",
      text: `by ${line.option_name}: ${plural(picks.size, "part")}`,
      title: [...picks].map(([id, vs]) => `${vs.join(", ")} → ${sku(id)}`).join("\n"),
    });
  }
  const overrides = ix.overridesByLine.get(line.id)?.size ?? 0;
  if (overrides) out.push({ kind: "override", text: plural(overrides, "variant change") });
  return out;
}

/** For an option line: each part it picks and the option values that pick it (the fallback part last). */
export function optionPicks(ix: BomIndex, line: BomLineRow, variants: readonly BomItem[]): { itemId: string | null; values: string[] }[] {
  if (!line.option_name) return [];
  const values = productOptions(variants).find((o) => o.name.toLowerCase() === line.option_name.toLowerCase())?.values ?? [];
  const picks = new Map<string | null, string[]>();
  for (const v of values) {
    const id = ix.mapsByLine.get(line.id)?.get(v.toLowerCase()) ?? line.component_item_id ?? null;
    picks.set(id, [...(picks.get(id) ?? []), v]);
  }
  return [...picks].map(([itemId, vs]) => ({ itemId, values: vs }));
}

/** Variants as grid columns: by the product's first option, then the next ("Wireless: 5 colors | Wired: 5 colors"). */
export function variantColumns(variants: readonly BomItem[]): {
  groups: { name: string; value: string; variants: BomItem[] }[];
  options: { name: string; values: string[] }[];
} {
  const options = productOptions(variants);
  const rank = (v: BomItem) => {
    const o = variantOptions(v);
    return options.map((opt) => {
      const value = Object.entries(o).find(([k]) => k.toLowerCase() === opt.name.toLowerCase())?.[1];
      const i = value === undefined ? -1 : opt.values.indexOf(value);
      return i < 0 ? 1e6 : i;
    });
  };
  const sorted = [...variants].sort((a, b) => {
    const [x, y] = [rank(a), rank(b)];
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i];
    return bySku(a, b);
  });
  const first = options[0];
  if (!first || options.length < 2) return { groups: [{ name: first?.name ?? "", value: "", variants: sorted }], options };
  const groups = first.values.map((value) => ({
    name: first.name,
    value,
    variants: sorted.filter((v) => Object.entries(variantOptions(v)).some(([k, x]) => k.toLowerCase() === first.name.toLowerCase() && x === value)),
  }));
  const rest = sorted.filter((v) => !groups.some((g) => g.variants.includes(v)));
  if (rest.length) groups.push({ name: first.name, value: "Other", variants: rest });
  return { groups: groups.filter((g) => g.variants.length), options };
}

/** A variant's label in a grid column: its values for every option but the grouping one ("Ghost Black"). */
export function columnLabel(v: BomItem, groupOption: string): string {
  const rest = Object.entries(variantOptions(v)).filter(([k]) => k.toLowerCase() !== groupOption.toLowerCase());
  return rest.map(([, x]) => x).join(" / ") || v.shopify_variant_title || v.sku;
}

// Used in -----------------------------------------------------------------------------------------------

export type UsedIn = { projects: string[]; products: string[] };

/**
 * Where each part ends up, through sub-assemblies, at current revisions: the projects whose builds use it
 * (partProjects()) and the top-level products (or sub-assemblies nothing else uses). Built once for the Parts library;
 * a part no current revision uses isn't in it.
 */
export function usedInIndex(px: ProjectIndex): Map<string, UsedIn> {
  const ix = px.ix;
  const projectsOf = partProjects(px);
  const parents = new Map<string, Set<string>>(); // item → family keys that use it directly
  const keys = [...ix.families.keys()];
  for (const key of keys) {
    const bom = currentRevision(ix, key);
    if (!bom) continue;
    for (const line of ix.linesByBom.get(bom.id) ?? []) {
      const ids = [
        line.component_item_id,
        ...(ix.mapsByLine.get(line.id)?.values() ?? []),
        ...[...(ix.overridesByLine.get(line.id)?.values() ?? [])].map((o) => o.component_item_id),
      ];
      for (const id of ids) if (id) (parents.get(id) ?? parents.set(id, new Set()).get(id))?.add(key);
    }
  }
  const memo = new Map<string, UsedIn>();
  const walk = (itemId: string, seen: Set<string>): UsedIn => {
    const hit = memo.get(itemId);
    if (hit) return hit;
    const products = new Set<string>();
    for (const key of parents.get(itemId) ?? []) {
      const owner = key.startsWith("item:") ? key.slice(5) : null;
      const up = owner && !seen.has(owner) ? walk(owner, new Set(seen).add(owner)) : null;
      if (up?.products.length) for (const p of up.products) products.add(p);
      else {
        const bom = currentRevision(ix, key);
        if (bom) products.add(ownerLabel(ix, bom));
      }
    }
    const out = { projects: projectsOf.get(itemId)?.inUse ?? [], products: [...products].sort() };
    memo.set(itemId, out);
    return out;
  };
  const out = new Map<string, UsedIn>();
  for (const id of parents.keys()) out.set(id, walk(id, new Set([id])));
  return out;
}

/** Built: a Shopify item with a BOM, or one in a project (its own, or its product linked to one). */
export function isBuilt(px: ProjectIndex, item: Pick<Item, "id" | "source" | "shopify_product_id" | "project_id" | "kind">): boolean {
  if (item.source === "shopify" && item.project_id) return true;
  if (item.shopify_product_id && (px.linkByProduct.has(item.shopify_product_id) || px.ix.families.has(`product:${item.shopify_product_id}`))) return true;
  return item.kind === "subassembly" && px.ix.families.has(`item:${item.id}`);
}

/** The project a Shopify item is in through its product, else its own; null for a part (it has projects: partProjects()). */
export const projectOfItem = (px: ProjectIndex, item: Pick<Item, "source" | "shopify_product_id" | "project_id">) =>
  item.source === "local"
    ? null
    : ((item.shopify_product_id ? px.linkByProduct.get(item.shopify_product_id)?.project_id : undefined) ?? item.project_id ?? null);

/**
 * The one project filter for lists (?project=<id|none>): a part by its projects, in use or only on a draft; a sell item
 * by projectOfItem(), or as a part of the project's builds (a Shopify joystick on its BOM, as the Sell items list always
 * had it). "none": in no project.
 */
export function inProject(px: ProjectIndex, item: Pick<Item, "id" | "source" | "shopify_product_id" | "project_id">, projectId: string): boolean {
  const reach = reachOf(px);
  if (item.source !== "local") {
    const own = projectOfItem(px, item);
    const used = (p: ProjectReach) => p.parts.has(item.id) || p.onDraft.has(item.id);
    if (projectId === "none") return !own && ![...reach.byProject.values()].some(used);
    const p = reach.byProject.get(projectId);
    return own === projectId || (!!p && used(p));
  }
  return partInProject(reach, item.id, projectId);
}

/** A project found by its SKU prefix (any case) or id. */
export function projectByCode<P extends Pick<Project, "id" | "code">>(projects: readonly P[], codeOrId: string): P | undefined {
  const want = codeOrId.trim().toLowerCase();
  return projects.find((p) => p.code && p.code.toLowerCase() === want) ?? projects.find((p) => p.id === codeOrId);
}

/** Where a project's workspace lives: /projects/GCU2 (its id when it has no SKU prefix). */
export const projectPath = (p: Pick<Project, "id" | "code">, tab?: string) => `/projects/${encodeURIComponent(p.code || p.id)}${tab ? `?tab=${tab}` : ""}`;

/** Where a gap is fixed, as an app path (the workspace opens items and specs in place instead). */
export function targetPath(px: ProjectIndex, project: Pick<Project, "id" | "code">, t: GapTarget): string {
  switch (t.kind) {
    case "basics":
      return projectPath(project); // Configure → Basics
    case "products":
      return projectPath(project, "products");
    case "bom":
      return `/boms?open=${t.id}`;
    case "item": {
      const item = px.itemById.get(t.id);
      return item?.source === "shopify" ? `/items/sell?open=${t.id}` : `/parts?open=${t.id}`;
    }
    case "spec":
      return `/specs?open=${t.id}`;
    case "cm_site":
      return "/consignment?setup=new";
    case "consign":
      return t.locationId ? `/consignment?cm=${t.locationId}` : "/consignment";
    case "make":
      return `/make?project=${project.id}`;
  }
}
